import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import * as minutesService from '../../services/caseRequestMinutes';
import type { CaseRequestMinute } from '../../types/caseRequestMinutes';
import { formatCommunicationTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { printPdfBlob } from '../../utils/printPdfBlob';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';
import TaskCaseMinutesSubTasksSection from './TaskCaseMinutesSubTasksSection';

const MAX_MINUTE_LENGTH = 2000;

type Props = {
  caseRequestId: string;
  readOnly?: boolean;
};

type MinuteRow = {
  id: string;
  authorLabel: string;
  time: string;
  text: string;
  sortKey: string;
};

function authorLabel(minute: CaseRequestMinute): string {
  const name = minute.createdByName.trim();
  const role = minute.createdByRole.trim();
  if (name && role) return `${name} - ${role}`;
  return name || role || '—';
}

function mapApiMinute(minute: CaseRequestMinute): MinuteRow {
  return {
    id: minute.id,
    authorLabel: authorLabel(minute),
    time: formatCommunicationTime(minute.createdAt),
    text: minute.comment,
    sortKey: minute.createdAt,
  };
}

function sortMinutes(rows: MinuteRow[]): MinuteRow[] {
  return [...rows].sort((a, b) => b.sortKey.localeCompare(a.sortKey));
}

export default function TaskCaseMinutesTab({
  caseRequestId,
  readOnly = false,
}: Props) {
  const { t } = useTranslation();
  const canLoad = isCaseRequestUuid(caseRequestId);
  const [loading, setLoading] = useState(canLoad);
  const [loadError, setLoadError] = useState('');
  const [minutes, setMinutes] = useState<MinuteRow[]>([]);
  const [minuteText, setMinuteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const loadMinutes = useCallback(async () => {
    if (!canLoad) {
      setLoading(false);
      setLoadError('Case request id is missing or invalid.');
      return;
    }

    setLoading(true);
    setLoadError('');
    const r = await minutesService.getCaseRequestMinutes(caseRequestId);
    setLoading(false);
    if (!r.success) {
      const msg = r.message ?? 'Failed to load case minutes.';
      setLoadError(msg);
      setFlashToast({ message: msg, variant: 'error' });
      return;
    }
    setMinutes(sortMinutes(r.data.minutes.map(mapApiMinute)));
  }, [canLoad, caseRequestId]);

  useEffect(() => {
    void loadMinutes();
  }, [loadMinutes]);

  const submitMinute = async () => {
    const text = minuteText.trim();
    if (!text) {
      setFlashToast({ message: 'Please enter case minutes.', variant: 'error' });
      return;
    }
    if (text.length > MAX_MINUTE_LENGTH) {
      setFlashToast({
        message: `Case minutes must be ${MAX_MINUTE_LENGTH} characters or fewer.`,
        variant: 'error',
      });
      return;
    }
    if (submitting || !canLoad) return;

    setSubmitting(true);
    const r = await minutesService.addCaseRequestMinute(caseRequestId, text);
    setSubmitting(false);

    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Could not add case minutes.', variant: 'error' });
      return;
    }

    setMinuteText('');
    setFlashToast({ message: 'Case minutes added successfully.', variant: 'success' });
    void loadMinutes();
  };

  if (!canLoad) {
    return <p className="tcd-doc-empty-msg">Case request id is missing or invalid.</p>;
  }

  const handleCancelClick = () => {
    if (minuteText.trim()) {
      setCancelConfirmOpen(true);
      return;
    }
    setMinuteText('');
  };

  const handlePrint = async () => {
    if (!canLoad || printing) return;

    setPrinting(true);
    const r = await minutesService.getCaseRequestMinutesPdf(caseRequestId);
    setPrinting(false);

    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Could not load case minutes PDF.', variant: 'error' });
      return;
    }

    printPdfBlob(r.data, 'Case-Minutes');
  };

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setMinuteText('');
          setCancelConfirmOpen(false);
        }}
      />

      <div className="tcd-minutes">
        <div className="tcd-minutes-hdr">
          <h3 className="tcd-minutes-title">{t('tasks.details.tabCaseMinutes')}</h3>
          <div className="tcd-minutes-toolbar">
            <button
              type="button"
              className="btn-ca tcd-letter-print-btn tcd-minutes-print-btn"
              disabled={!canLoad || printing}
              onClick={() => void handlePrint()}
            >
              {printing ? 'Preparing…' : 'Print'}
            </button>
          </div>
        </div>
        {loading ? (
          <p className="tcd-loading">Loading case minutes…</p>
        ) : loadError ? (
          <p className="tcd-error" role="alert">{loadError}</p>
        ) : minutes.length ? (
          minutes.map((entry) => (
            <div key={entry.id} className="min-entry">
              <div className="min-meta">
                <span className="min-user">{entry.authorLabel}</span>
                <span className="min-ts">{entry.time}</span>
              </div>
              <div className="min-text">{entry.text}</div>
            </div>
          ))
        ) : (
          <p style={{ fontSize: 12, color: 'var(--t3)', margin: '0 0 10px' }}>{t('common.noDataFound')}</p>
        )}

        {!readOnly && !loading && !loadError ? (
          <>
            <div className="min-add-title">Add Case Minutes</div>
            <textarea
              className="note-ta"
              rows={5}
              placeholder="Add Case Minutes here..."
              value={minuteText}
              disabled={submitting}
              maxLength={MAX_MINUTE_LENGTH}
              onChange={(e) => setMinuteText(e.target.value)}
            />
            <div className="tcd-comm-actions">
              <button type="button" className="btn-cn2" disabled={submitting} onClick={handleCancelClick}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-add-min"
                disabled={submitting || !minuteText.trim()}
                onClick={() => void submitMinute()}
              >
                {submitting ? 'Adding…' : 'Add Case Minutes'}
              </button>
            </div>
          </>
        ) : null}

        <TaskCaseMinutesSubTasksSection caseRequestId={caseRequestId} />
      </div>
    </>
  );
}
